import { useState, useEffect, useCallback } from 'react';
import { listDocuments, getDocumentFileUrl, getDocumentStatus, getErrorMessage } from '../api/documents';
import { useAuth } from '../context/AuthContext';

// Bibliothèque de documents — lecture seule. Chaque utilisateur ne voit que les types
// de documents que l'Administrateur a autorisés pour son niveau.

// Détermine si un document est un PDF (donc affichable dans le navigateur) à partir
// des informations disponibles côté frontend — sans dépendre du Content-Type renvoyé
// par le serveur, qui peut être générique. On regarde le type/libellé/nom du document.
function isPdf(doc) {
  const clues = [doc.doc_type, doc.type, doc.doc_type_label, doc.filename, doc.original_name, doc.title]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return clues.includes('pdf');
}

// Déduit un nom de fichier à proposer au téléchargement (avec extension si on la connaît).
function suggestedFileName(doc) {
  if (doc.original_name) return doc.original_name;
  if (doc.filename) return doc.filename;
  const base = doc.title || 'document';
  if (doc.doc_type_label && /\.pcs/i.test(doc.doc_type_label)) return `${base}.pcs`;
  return base;
}

export default function Documents() {
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [connected, setConnected] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [operationResult, setOperationResult] = useState('');

  const load = useCallback((background = false) => {
    if (!background) setLoading(true);
    Promise.all([listDocuments(token), getDocumentStatus(token)])
      .then(([docs]) => {
        setDocuments(docs);
        setConnected(true);
        setError('');
      })
      .catch((err) => {
        setConnected(false);
        setError(getErrorMessage(err, 'Backend déconnecté — dernière liste connue conservée.'));
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    load();
    const refresh = setInterval(() => load(true), 5000);
    return () => clearInterval(refresh);
  }, [load]);

  const handleOpen = async (doc) => {
    if (!connected || openingId !== null) return;
    setOpeningId(doc.id);
    setOperationResult('');
    try {
      if (doc.external_link) {
        window.open(doc.external_link, '_blank', 'noopener,noreferrer');
        return;
      }

      const url = await getDocumentFileUrl(token, doc.id);

      if (isPdf(doc)) {
        // PDF : le navigateur sait l'afficher, on l'ouvre dans un nouvel onglet.
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        // Tout le reste (.pcs, etc.) : le navigateur ne sait pas l'afficher —
        // on force le téléchargement au lieu de tenter de le rendre à l'écran.
        const a = document.createElement('a');
        a.href = url;
        a.download = suggestedFileName(doc);
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      setOperationResult('Document préparé avec succès.');
    } catch (err) {
      setError(getErrorMessage(err, "Impossible d'ouvrir ce document."));
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <div className="admin-page">
      <div className="home-header">
        <h1>Documents</h1>
        <p>Documents techniques et pédagogiques mis à votre disposition</p>
        <span className="scada-badge">S C A D A</span>
      </div>

      <div className="admin-card admin-card-wide">
        <div className={connected ? 'document-status connected' : 'document-status disconnected'}>
          {connected ? 'BACKEND CONNECTÉ — SOURCE : SERVEUR' : 'BACKEND DÉCONNECTÉ — OPÉRATIONS DÉSACTIVÉES'}
        </div>
        {error && <div className="auth-error">{error}</div>}
        {operationResult && <div className="auth-success">{operationResult}</div>}
        {!connected && <button className="btn-primary" onClick={() => load()}>Réessayer</button>}

        {loading ? (
          <p className="admin-empty">Chargement…</p>
        ) : documents.length === 0 ? (
          <p className="admin-empty">Aucun document disponible pour votre niveau pour le moment.</p>
        ) : (
          <div className="documents-table-wrap">
          <table className="admin-table documents-table">
            <thead>
              <tr>
                <th>Titre</th>
                <th>Type</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {documents.map((d) => (
                <tr key={d.id}>
                  <td>{d.title}</td>
                  <td><span className="role-badge role-niveau">{d.doc_type_label}</span></td>
                  <td>
                    <button className="btn-primary" disabled={!connected || openingId !== null} onClick={() => handleOpen(d)}>
                      {openingId === d.id ? 'Préparation…' : d.external_link ? 'Ouvrir le lien' : isPdf(d) ? 'Consulter' : 'Télécharger'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
